// Phase 6.B-4 — 직원 CRUD UI.
//
// 시안 1:1 port: design/v3/SettingsExtraSections.jsx:163-277 (StaffSection).
// 단일 list (Card) + 행 클릭 → 편집 Modal. 모바일 대응은 후속 단계 (현재는 desktop 우선).
//   - "+ 직원 초대" → Modal: 이름·이메일·역할·매장 scope·연락처·알림 채널
//   - 행 편집 → 같은 Modal (편집 모드, 이메일 readonly)
//   - 삭제 (membership disable, soft revoke) → window.confirm
//   - 빈 상태 → EmptyState
//
// 초대 흐름:
//   - 백엔드 staffControllerCreate 가 console.log 로 초대 링크 출력 (Phase 7 메일 mock).
//   - 응답 envelope: { item: {...membership, inviteLink: 'https://app.dvvb.io/invite/<token>'} }.
//   - 클라이언트는 toast 로 "초대 링크: ..." 안내.
//
// API:
//   - staffControllerList()        queryKey ['staff', 'list']
//   - staffControllerCreate({body})
//   - staffControllerUpdate({path, body})
//   - staffControllerRemove({path})
//
// RBAC: owner-only mutation. staff 본인은 list 조회 가능 (백엔드 RLS 처리).

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  staffControllerCreate,
  staffControllerList,
  staffControllerRemove,
  staffControllerUpdate,
  storesControllerList,
} from '@dvvb/sdk';

import {
  AlertBanner,
  Badge,
  Button,
  EmptyState,
  FormField,
  Icon,
  Modal,
  Select,
  TextInput,
  Toggle,
  useToast,
} from '../../components/primitives';
import { ApiCallError, unwrap } from '../../lib/envelope';
import { useAuth } from '../../auth/AuthContext';
import { queryClient } from '../../lib/queryClient';
import type { StoreRow } from '../../types/api';

type StaffRole = 'owner' | 'staff';
type StaffStatus = 'active' | 'invited' | 'disabled';
type WorkGroup = 'morning' | 'afternoon' | 'prep' | 'all';

interface StaffProfile {
  phone: string | null;
  smsEnabled: boolean;
  kakaoUserId: string | null;
  telegramChatId: string | null;
  workGroup: WorkGroup | null;
  isActive: boolean;
  metadata?: Record<string, unknown>;
}

interface StaffRow {
  id: string; // membership id
  userId: string;
  companyId: string;
  role: StaffRole;
  status: StaffStatus;
  storeScope: string[];
  invitedById: string | null;
  invitedAt: string | null;
  acceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: { id: string; email: string; name: string | null; status: string } | null;
  staffProfile: StaffProfile | null;
}

interface StaffEnvelope {
  items: StaffRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

interface StoresEnvelope {
  items: StoreRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

const WORK_GROUP_OPTIONS: Array<{ value: WorkGroup | ''; label: string }> = [
  { value: '', label: '미지정' },
  { value: 'morning', label: '오전조' },
  { value: 'afternoon', label: '오후조' },
  { value: 'prep', label: '전처리조' },
  { value: 'all', label: '전체' },
];

const ROLE_OPTIONS: Array<{ value: StaffRole; label: string }> = [
  { value: 'staff', label: '직원' },
  { value: 'owner', label: '사장 (owner)' },
];

const STATUS_LABEL: Record<StaffStatus, string> = {
  active: '활성',
  invited: '초대됨',
  disabled: '비활성',
};

const STATUS_BADGE: Record<StaffStatus, 'success' | 'warning' | 'default'> = {
  active: 'success',
  invited: 'warning',
  disabled: 'default',
};

const TABLE_GRID =
  'minmax(180px, 1.4fr) 110px 110px 160px 160px 90px';

export function SettingsStaffRoute() {
  const auth = useAuth();
  const isOwner = auth.role === 'owner';
  const toast = useToast();

  const [modalState, setModalState] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; staff: StaffRow }
    | null
  >(null);

  const listQ = useQuery({
    queryKey: ['staff', 'list'],
    queryFn: async () => {
      const r = await staffControllerList({
        query: { limit: '500' } as never,
      });
      return unwrap<StaffEnvelope>(r);
    },
  });

  const storesQ = useQuery({
    queryKey: ['stores', 'list'],
    queryFn: async () => {
      const r = await storesControllerList({
        query: { limit: '500' } as never,
      });
      return unwrap<StoresEnvelope>(r);
    },
  });

  const removeMu = useMutation({
    mutationFn: async (id: string) => {
      const r = await staffControllerRemove({ path: { id } });
      return unwrap<{ deleted: boolean; id: string }>(r);
    },
    onSuccess: async () => {
      toast.success('직원을 비활성화했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '직원 비활성화 실패');
    },
  });

  const staff = listQ.data?.items ?? [];
  const stores = storesQ.data?.items ?? [];

  if (listQ.isLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
        직원 목록을 불러오는 중…
      </div>
    );
  }
  if (listQ.isError) {
    return (
      <AlertBanner
        tone="danger"
        message={`직원 목록을 불러오지 못했습니다: ${
          (listQ.error as ApiCallError | undefined)?.message ?? '알 수 없는 오류'
        }`}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHead
        title="직원"
        sub={
          <>
            등록한 직원에게 <b style={{ color: 'var(--fg-1)' }}>작업일정</b>을
            발송합니다. 직원별로 받을 채널(문자 · 카카오톡 · 텔레그램)을
            선택하세요.
          </>
        }
        action={
          <Button
            variant="primary"
            size="md"
            disabled={!isOwner}
            title={!isOwner ? '권한 없음 (owner 전용)' : ''}
            onClick={() => setModalState({ mode: 'create' })}
          >
            + 직원 초대
          </Button>
        }
      />

      {staff.length === 0 ? (
        <EmptyState
          icon="user"
          title="등록된 직원이 없습니다"
          description="직원을 초대하면 일정·알림이 자동 발송됩니다."
          action={
            isOwner ? (
              <Button
                variant="primary"
                onClick={() => setModalState({ mode: 'create' })}
              >
                + 직원 초대
              </Button>
            ) : null
          }
        />
      ) : (
        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-weak)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: TABLE_GRID,
              gap: 12,
              padding: '10px 16px',
              background: 'var(--bg-subtle)',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--fg-3)',
              textTransform: 'uppercase',
              letterSpacing: 'var(--tracking-wide)',
            }}
          >
            <div>이름 / 이메일</div>
            <div>역할</div>
            <div>상태</div>
            <div>조 / 매장</div>
            <div>알림 채널</div>
            <div></div>
          </div>
          {staff.map((s, i) => (
            <StaffRowView
              key={s.id}
              row={s}
              first={i === 0}
              isOwner={isOwner}
              onEdit={() => setModalState({ mode: 'edit', staff: s })}
              onRemove={() => {
                const name = s.user?.name ?? s.user?.email ?? s.id;
                if (
                  !window.confirm(`"${name}" 직원을 비활성화하시겠습니까?`)
                )
                  return;
                removeMu.mutate(s.id);
              }}
              removing={removeMu.isPending}
            />
          ))}
        </div>
      )}

      {/* Channel guide banner */}
      <div
        style={{
          padding: '12px 14px',
          borderRadius: 10,
          background: 'var(--info-bg)',
          border: '1px solid var(--info)',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
          fontSize: 12,
          color: 'var(--fg-2)',
          lineHeight: 1.6,
        }}
      >
        <Icon name="alert" size={14} color="var(--info)" />
        <div>
          <b style={{ color: 'var(--fg-1)' }}>알림 채널 안내</b>
          <br />· <b>문자</b>: 모든 휴대폰. 일정 + 체크리스트 링크.
          <br />· <b>카카오톡</b>: 알림톡 (사전 등록된 템플릿). 발송비 ₩9/건.
          <br />· <b>텔레그램</b>: 봇 연결 필요 — 직원이 봇에 1회 메시지 보내면
          자동 연결.
        </div>
      </div>

      {modalState && (
        <StaffFormModal
          state={modalState}
          stores={stores}
          isOwner={isOwner}
          onClose={() => setModalState(null)}
          onSaved={() => setModalState(null)}
        />
      )}
    </div>
  );
}

function StaffRowView({
  row,
  first,
  isOwner,
  onEdit,
  onRemove,
  removing,
}: {
  row: StaffRow;
  first: boolean;
  isOwner: boolean;
  onEdit: () => void;
  onRemove: () => void;
  removing: boolean;
}) {
  const name = row.user?.name ?? row.user?.email ?? '(이름 없음)';
  const email = row.user?.email ?? '';
  const profile = row.staffProfile;
  const channels: string[] = [];
  if (profile?.smsEnabled) channels.push('문자');
  if (profile?.kakaoUserId) channels.push('카카오톡');
  if (profile?.telegramChatId) channels.push('텔레그램');

  const wgLabel =
    WORK_GROUP_OPTIONS.find((o) => o.value === (profile?.workGroup ?? ''))?.label ??
    '미지정';
  const scopeLabel =
    row.storeScope.length === 0
      ? '전체 매장'
      : `매장 ${row.storeScope.length}개`;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: TABLE_GRID,
        gap: 12,
        padding: '12px 16px',
        alignItems: 'center',
        borderTop: first ? 'none' : '1px solid var(--border-weak)',
        fontSize: 13,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: 999,
            background: 'var(--primary-strong)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {(name[0] ?? '?').toUpperCase()}
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              color: 'var(--fg-1)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              fontFamily: 'var(--font-mono)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {email}
          </div>
        </div>
      </div>
      <div style={{ color: 'var(--fg-2)' }}>
        {row.role === 'owner' ? '사장' : '직원'}
      </div>
      <div>
        <Badge variant={STATUS_BADGE[row.status]} dot>
          {STATUS_LABEL[row.status]}
        </Badge>
      </div>
      <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>
        <div>{wgLabel}</div>
        <div style={{ color: 'var(--fg-3)', fontSize: 11, marginTop: 2 }}>
          {scopeLabel}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          fontSize: 11,
          color: 'var(--fg-3)',
        }}
      >
        {channels.length === 0 ? (
          <span style={{ color: 'var(--fg-4)' }}>—</span>
        ) : (
          channels.map((c) => (
            <span
              key={c}
              style={{
                padding: '2px 8px',
                borderRadius: 999,
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border-weak)',
                color: 'var(--fg-2)',
              }}
            >
              {c}
            </span>
          ))
        )}
      </div>
      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        <Button
          variant="secondary"
          size="sm"
          disabled={!isOwner}
          title={!isOwner ? '권한 없음 (owner 전용)' : ''}
          onClick={onEdit}
        >
          편집
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!isOwner || removing}
          title={!isOwner ? '권한 없음 (owner 전용)' : ''}
          onClick={onRemove}
          style={{ color: 'var(--danger)' }}
        >
          삭제
        </Button>
      </div>
    </div>
  );
}

function StaffFormModal({
  state,
  stores,
  isOwner,
  onClose,
  onSaved,
}: {
  state: { mode: 'create' } | { mode: 'edit'; staff: StaffRow };
  stores: StoreRow[];
  isOwner: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const editing = state.mode === 'edit';
  const initial = editing ? state.staff : null;
  const initialProfile = initial?.staffProfile ?? null;

  const [name, setName] = useState(initial?.user?.name ?? '');
  const [email, setEmail] = useState(initial?.user?.email ?? '');
  const [role, setRole] = useState<StaffRole>(initial?.role ?? 'staff');
  const [storeScope, setStoreScope] = useState<string[]>(
    initial?.storeScope ?? [],
  );
  const [phone, setPhone] = useState(initialProfile?.phone ?? '');
  const [workGroup, setWorkGroup] = useState<WorkGroup | ''>(
    initialProfile?.workGroup ?? '',
  );
  const [smsEnabled, setSmsEnabled] = useState(
    initialProfile?.smsEnabled ?? false,
  );
  const [kakaoUserId, setKakaoUserId] = useState(
    initialProfile?.kakaoUserId ?? '',
  );
  const [telegramChatId, setTelegramChatId] = useState(
    initialProfile?.telegramChatId ?? '',
  );

  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  const createMu = useMutation({
    mutationFn: async () => {
      const r = await staffControllerCreate({
        body: {
          email: email.trim(),
          name: name.trim(),
          role,
          storeScope,
          staffProfile: {
            phone: phone.trim() || undefined,
            smsEnabled,
            kakaoUserId: kakaoUserId.trim() || undefined,
            telegramChatId: telegramChatId.trim() || undefined,
            workGroup: workGroup || undefined,
            isActive: true,
          },
        } as never,
      });
      return unwrap<{ item: StaffRow & { inviteLink?: string } }>(r);
    },
    onSuccess: async (res) => {
      const link = res?.item?.inviteLink;
      if (link) {
        toast.success(`초대 완료. 링크 (mock): ${link}`);
        // eslint-disable-next-line no-console
        console.log('[invite-mock]', link);
      } else {
        toast.success('직원을 초대했습니다.');
      }
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
      onSaved();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '직원 초대 실패');
    },
  });

  const updateMu = useMutation({
    mutationFn: async () => {
      if (!initial) throw new Error('no initial');
      const r = await staffControllerUpdate({
        path: { id: initial.id },
        body: {
          role,
          storeScope,
          staffProfile: {
            phone: phone.trim() || undefined,
            smsEnabled,
            kakaoUserId: kakaoUserId.trim() || undefined,
            telegramChatId: telegramChatId.trim() || undefined,
            workGroup: workGroup || undefined,
          },
        } as never,
      });
      return unwrap<{ item: StaffRow }>(r);
    },
    onSuccess: async () => {
      toast.success('직원 정보를 저장했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
      onSaved();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '직원 정보 저장 실패');
    },
  });

  const submitting = createMu.isPending || updateMu.isPending;

  function submit() {
    const next: typeof errors = {};
    if (!name.trim()) next.name = '이름은 필수입니다.';
    if (!editing) {
      if (!email.trim()) next.email = '이메일은 필수입니다.';
      else if (!/.+@.+\..+/.test(email.trim()))
        next.email = '올바른 이메일 형식이 아닙니다.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    if (editing) updateMu.mutate();
    else createMu.mutate();
  }

  function toggleStore(id: string) {
    setStoreScope((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={editing ? '직원 정보 편집' : '직원 초대'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            취소
          </Button>
          <Button
            variant="strong"
            onClick={submit}
            disabled={submitting || !isOwner}
            title={!isOwner ? '권한 없음 (owner 전용)' : ''}
          >
            {submitting ? '저장 중…' : editing ? '저장' : '초대 보내기'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 14,
          }}
        >
          <FormField label="이름" required error={errors.name}>
            <TextInput
              value={name}
              onChange={setName}
              placeholder="예: 박민수"
              error={!!errors.name}
              autoComplete="off"
              disabled={editing}
            />
          </FormField>
          <FormField label="이메일" required error={errors.email}>
            <TextInput
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="staff@example.com"
              error={!!errors.email}
              autoComplete="off"
              disabled={editing}
            />
          </FormField>
          <FormField label="역할" required>
            <Select
              value={role}
              onChange={(v) => setRole(v as StaffRole)}
              options={ROLE_OPTIONS}
            />
          </FormField>
          <FormField label="조">
            <Select
              value={workGroup}
              onChange={(v) => setWorkGroup(v as WorkGroup | '')}
              options={WORK_GROUP_OPTIONS}
            />
          </FormField>
          <FormField label="연락처">
            <TextInput
              type="tel"
              value={phone}
              onChange={setPhone}
              placeholder="010-1234-5678"
              autoComplete="off"
            />
          </FormField>
          <FormField label="카카오톡 사용자 ID">
            <TextInput
              value={kakaoUserId}
              onChange={setKakaoUserId}
              placeholder="알림톡 ID (선택)"
              autoComplete="off"
            />
          </FormField>
        </div>

        <FormField label="텔레그램 chat ID" hint="봇 연결 후 자동으로 채워집니다.">
          <TextInput
            value={telegramChatId}
            onChange={setTelegramChatId}
            placeholder="예: 12345678 (선택)"
            autoComplete="off"
          />
        </FormField>

        <FormField
          label="문자 알림"
          hint="휴대폰 번호로 SMS 발송 (일정·체크리스트)"
        >
          <Toggle
            checked={smsEnabled}
            onChange={setSmsEnabled}
            label={smsEnabled ? '활성' : '비활성'}
          />
        </FormField>

        <FormField
          label="매장 권한"
          hint={
            stores.length === 0
              ? '등록된 매장이 없습니다.'
              : '선택 안 함 = 전체 매장'
          }
        >
          {stores.length === 0 ? (
            <div
              style={{
                fontSize: 12,
                color: 'var(--fg-3)',
                padding: '8px 12px',
                background: 'var(--bg-subtle)',
                borderRadius: 8,
                border: '1px solid var(--border-weak)',
              }}
            >
              매장을 먼저 등록하세요.
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
              }}
            >
              {stores.map((s) => {
                const on = storeScope.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleStore(s.id)}
                    style={{
                      padding: '5px 11px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: on ? 600 : 500,
                      background: on
                        ? 'var(--primary-bg)'
                        : 'var(--bg-surface)',
                      color: on ? 'var(--primary-strong)' : 'var(--fg-2)',
                      border: on
                        ? '1px solid var(--primary-border)'
                        : '1px solid var(--border-base)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-sans)',
                    }}
                  >
                    {s.brand ?? s.name}
                  </button>
                );
              })}
            </div>
          )}
        </FormField>

        {!editing && (
          <div
            style={{
              padding: '10px 12px',
              background: 'var(--info-bg)',
              border: '1px solid var(--info)',
              borderRadius: 'var(--radius-md)',
              fontSize: 12,
              color: 'var(--fg-2)',
              lineHeight: 1.5,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}
          >
            <Icon name="alert" size={14} color="var(--info)" />
            <div>
              초대 시 메일은 <b>발송되지 않습니다</b> (Phase 7 메일 인프라 예정).
              초대 링크는 콘솔/Toast 에 표시되니 직접 전달하세요.
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function SectionHead({
  title,
  sub,
  action,
}: {
  title: string;
  sub?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--fg-1)',
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--fg-3)',
              marginTop: 4,
              lineHeight: 1.5,
              maxWidth: 560,
            }}
          >
            {sub}
          </div>
        )}
      </div>
      {action}
    </div>
  );
}

